import { useEffect } from "react";

import { hasMod, isEditableTarget } from "../lib/platform";

export interface GlobalShortcutHandlers {
  goHome: () => void;
  goSearch: () => void;
  newDocument: () => void;
  openShortcuts: () => void;
  openCommandPalette: () => void;
  toggleSidebar: () => void;
  toggleTheme: () => void;
}

/**
 * Application-wide key handling. Keystrokes aimed at a text field or a
 * contenteditable surface are always left alone, so typing "d" in the search box
 * does not navigate away.
 */
export function useGlobalShortcuts(handlers: GlobalShortcutHandlers): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;

      const mod = hasMod(event);
      const key = event.key;

      if (mod && !event.shiftKey && !event.altKey && key.toLowerCase() === "k") {
        event.preventDefault();
        handlers.openCommandPalette();
        return;
      }
      if (mod && key === ".") {
        event.preventDefault();
        handlers.toggleSidebar();
        return;
      }
      if (mod && event.shiftKey && key.toLowerCase() === "l") {
        event.preventDefault();
        handlers.toggleTheme();
        return;
      }

      if (mod || event.altKey) return;
      if (isEditableTarget(event.target)) return;

      switch (key) {
        case "?":
          event.preventDefault();
          handlers.openShortcuts();
          return;
        case "/":
        case "t":
        case "T":
          event.preventDefault();
          handlers.goSearch();
          return;
        case "d":
        case "D":
          event.preventDefault();
          handlers.goHome();
          return;
        case "n":
        case "N":
          event.preventDefault();
          handlers.newDocument();
          return;
        default:
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [handlers]);
}
